import type { IIndexable } from "@common-shared/types/general.types.ts"

import type { AvailableHttpMethod } from "@front-shared/types/general.ts"

import { FRONT_GENERAL_CONFIG } from "../config/general.config"
import { UtilHelper } from "./util.helper"

export class HttpHelper {
    /**
     * Send request to server
     */
    static async sendRequest(
        url: string,
        data: IIndexable = {},
        method: AvailableHttpMethod = "GET",
        appendApiUrl?: string,
        headers = {},
        authorizationHeader?: string,
        errorToastrTimeout = FRONT_GENERAL_CONFIG.defaultToastrTimeout,
        ignoreToastrErrorCodes: number[] = [],
        onError?: (
            message: string,
            timeout?: number,
            withHtml?: boolean,
        ) => void,
        onWarning?: (
            message: string,
            timeout?: number,
            withHtml?: boolean,
        ) => void,
        onLogout?: () => void,
        authorizationHeaderKey?: string,
        withCookies = false,
        onRefreshedAccessTokenReceived?: (refreshedToken: string) => void,
        abortSignal?: AbortSignal,
        authRetryLimit = 10,
        authRetryCount = 0,
    ): Promise<any> {
        method = (method as any).toUpperCase()

        if (appendApiUrl && !url.startsWith("/")) {
            url += "/"
        }

        const requestInit: RequestInit = {
            method,

            ...(withCookies
                ? {
                      credentials: "include",
                  }
                : {}),
        }

        const requestInitHeaders: Record<string, string> = Object.assign(
            {},
            headers,
        )

        if (authorizationHeader) {
            requestInitHeaders[
                authorizationHeaderKey ||
                    FRONT_GENERAL_CONFIG.headers.authorization
            ] = authorizationHeader
        }

        if (method === "GET") {
            url = UtilHelper.appendQueryParams(url, data)
        } else {
            requestInit.body = JSON.stringify(data)

            requestInitHeaders[FRONT_GENERAL_CONFIG.headers.contentType] =
                "application/json"
        }

        requestInit.signal = abortSignal
        requestInit.headers = requestInitHeaders

        let result
        try {
            result = await fetch((appendApiUrl || "") + url, requestInit)
        } catch (e) {
            // Don't show error if it wasn't network, but abort by signal
            if (!abortSignal?.aborted) {
                onError?.("Failed to access API")
            }

            return {}
        }

        const response: IIndexable = await result.json()

        if (result.ok) {
            if (onRefreshedAccessTokenReceived && response.refreshedToken) {
                onRefreshedAccessTokenReceived(response.refreshedToken)
            }

            return response.data
        }

        if (result.status === 401 && authorizationHeader && onLogout) {
            // Retry on auth error for a few times
            if (authRetryCount < authRetryLimit) {
                await UtilHelper.sleep(1)

                return await HttpHelper.sendRequest(
                    url,
                    data,
                    method,
                    appendApiUrl,
                    headers,
                    authorizationHeader,
                    errorToastrTimeout,
                    ignoreToastrErrorCodes,
                    onError,
                    onWarning,
                    onLogout,
                    authorizationHeaderKey,
                    withCookies,
                    onRefreshedAccessTokenReceived,
                    abortSignal,
                    authRetryLimit,
                    authRetryCount + 1,
                )
            }

            onWarning?.("Your session has expired. Please log in again")

            onLogout()
        } else if (
            response.error &&
            !ignoreToastrErrorCodes.includes(result.status)
        ) {
            // We trust to put html into the page from our API
            onError?.(response.error, errorToastrTimeout, true)

            // Handle different custom 400 errors that require different actions
            // Controlled by data object in error response
            if (response.data) {
                return {
                    errorData: response.data,
                }
            }
        }

        return {}
    }
}
